import { inject, Injectable, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AuthService } from 'app/core/auth/auth.service';
import {
  IdleWarningDialogComponent,
  IdleWarningDialogData,
  IdleWarningDialogResult,
} from 'app/core/auth/idle/idle-warning-dialog.component';
import {
  IDLE_LAST_ACTIVITY_KEY,
  IDLE_LOGOUT_KEY,
  IDLE_TIMEOUT_MS,
  IDLE_WARNING_MS,
  isIdleExpired,
  markActivityNow,
  readLastActivityAt,
} from 'app/core/auth/idle/idle.constants';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
  'pointerdown',
] as const;

const CHECK_INTERVAL_MS = 1000;

/** Keeps mousemove from hammering localStorage. */
const ACTIVITY_WRITE_THROTTLE_MS = 5000;

/**
 * Signs the user out after IDLE_TIMEOUT_MS without input, in every open tab.
 * Background API calls and websocket messages never count as activity.
 */
@Injectable({ providedIn: 'root' })
export class IdleService {
  private _zone = inject(NgZone);
  private _dialog = inject(MatDialog);
  private _authService = inject(AuthService);

  private _started = false;
  private _lastWriteAt = 0;
  private _warningRef: MatDialogRef<IdleWarningDialogComponent, IdleWarningDialogResult> | null =
    null;

  start(): void {
    if (this._started || typeof window === 'undefined') {
      return;
    }
    this._started = true;

    this._zone.runOutsideAngular(() => {
      for (const type of ACTIVITY_EVENTS) {
        document.addEventListener(type, this._onActivity, { capture: true, passive: true });
      }
      document.addEventListener('visibilitychange', this._check);
      window.addEventListener('focus', this._check);
      window.addEventListener('pageshow', this._check);
      window.addEventListener('storage', this._onStorage);
      setInterval(this._check, CHECK_INTERVAL_MS);
    });
  }

  private _onActivity = (): void => {
    if (!this._authService.accessToken || this._warningRef) {
      return;
    }
    // Input arriving after the deadline (e.g. waking a laptop) must not revive the session.
    if (isIdleExpired()) {
      this._check();
      return;
    }
    const now = Date.now();
    if (now - this._lastWriteAt < ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }
    this._lastWriteAt = now;
    markActivityNow();
  };

  private _onStorage = (event: StorageEvent): void => {
    if (event.key === IDLE_LOGOUT_KEY && event.newValue) {
      this._zone.run(() => {
        this._closeWarning();
        this._authService.logoutThisTabAfterIdle();
      });
      return;
    }
    if (event.key === IDLE_LAST_ACTIVITY_KEY) {
      this._check();
    }
  };

  private _check = (): void => {
    if (!this._authService.accessToken) {
      if (this._warningRef) {
        this._zone.run(() => this._closeWarning());
      }
      return;
    }

    const last = readLastActivityAt();
    if (!last) {
      markActivityNow();
      return;
    }

    const idleMs = Date.now() - last;
    if (idleMs >= IDLE_TIMEOUT_MS) {
      this._zone.run(() => this._logout());
      return;
    }

    if (idleMs >= IDLE_TIMEOUT_MS - IDLE_WARNING_MS) {
      if (!this._warningRef) {
        this._zone.run(() => this._openWarning());
      }
    } else if (this._warningRef) {
      this._zone.run(() => this._closeWarning());
    }
  };

  private _openWarning(): void {
    const data: IdleWarningDialogData = {
      getRemainingMs: () => IDLE_TIMEOUT_MS - (Date.now() - readLastActivityAt()),
    };
    const ref = this._dialog.open<
      IdleWarningDialogComponent,
      IdleWarningDialogData,
      IdleWarningDialogResult
    >(IdleWarningDialogComponent, {
      width: '420px',
      data,
    });
    this._warningRef = ref;

    ref.afterClosed().subscribe((result) => {
      // _closeWarning() clears the ref first, so programmatic closes are not mistaken for the user.
      const closedByUser = this._warningRef === ref;
      if (!closedByUser) {
        return;
      }
      this._warningRef = null;
      if (result === 'logout' || isIdleExpired()) {
        this._logout();
        return;
      }
      // Backdrop click, Esc, or any click inside the popup all mean "stay signed in".
      this._lastWriteAt = Date.now();
      markActivityNow();
    });
  }

  private _closeWarning(): void {
    const ref = this._warningRef;
    this._warningRef = null;
    ref?.close();
  }

  private _logout(): void {
    this._closeWarning();
    this._authService.signOutAfterIdle();
  }
}
